import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Grid, Tabs, Tab, CircularProgress, Alert,
  ToggleButtonGroup, ToggleButton, Chip,
} from '@mui/material';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';

import {
  getOutreachCampaignsReport, getOutreachFunnelReport, getOutreachChannelsReport,
  getOutreachStagesReport, getOutreachSentimentReport, getOutreachFollowUpsReport,
  getOutreachTimingReport, getOutreachPersonaToneReport, getOutreachFailuresReport,
  getOutreachRepsReport,
} from '../../services/api';
import KpiCard from '../../components/reports/KpiCard';
import DistributionBar from '../../components/reports/DistributionBar';
import FunnelChart from '../../components/reports/FunnelChart';
import TableWithBars from '../../components/reports/TableWithBars';
import Heatmap from '../../components/reports/Heatmap';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import ReportExportButton from '../../components/reports/ReportExportButton';

const fmtPct = (v, d = 1) => v == null ? '—' : `${(v * 100).toFixed(d)}%`;
const fmtInt = (v) => v == null ? '—' : Number(v).toLocaleString();
const fmtHours = (v) => v == null ? '—' : `${Number(v).toFixed(1)}h`;

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function ReportSection({ title, action, children }) {
  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function useReport(loader, filters) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await loader(filters);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.title || e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [loader, JSON.stringify(filters)]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, error };
}

// O1
function CampaignsCard({ filters }) {
  const { data, loading, error } = useReport(getOutreachCampaignsReport, filters);
  const rows = data?.data?.campaigns || [];
  return (
    <ReportSection
      title="O1 · Campaign Performance"
      action={<ReportExportButton path="/reports/outreach/campaigns/export.csv" filters={filters} filename="outreach-campaigns.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <TableWithBars
          barColumn="recipients"
          columns={[
            { key: 'name', label: 'Campaign' },
            { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} /> },
            { key: 'recipients', label: 'Recipients', align: 'right' },
            { key: 'sent', label: 'Sent', align: 'right' },
            { key: 'openRate', label: 'Open %', align: 'right', render: (r) => fmtPct(r.openRate) },
            { key: 'replyRate', label: 'Reply %', align: 'right', render: (r) => fmtPct(r.replyRate) },
            { key: 'bounceRate', label: 'Bounce %', align: 'right', render: (r) => fmtPct(r.bounceRate) },
            { key: 'positiveOutcomeRate', label: 'Pos %', align: 'right', render: (r) => fmtPct(r.positiveOutcomeRate) },
          ]}
          rows={rows.map((r) => ({ id: r.campaignId, ...r }))}
        />
      )}
    </ReportSection>
  );
}

// O2
function FunnelCard({ filters }) {
  const { data, loading, error } = useReport(getOutreachFunnelReport, filters);
  const stages = data?.data?.stages || [];
  const bounced = data?.data?.bounced || 0;
  const failed = data?.data?.failed || 0;
  return (
    <ReportSection title="O2 · Outreach Funnel">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <FunnelChart
            stages={stages.map((s) => ({ label: s.step, count: s.count }))}
            color="#16a34a"
          />
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Chip label={`Bounced: ${bounced}`} color={bounced > 0 ? 'warning' : 'default'} size="small" />
            <Chip label={`Failed: ${failed}`} color={failed > 0 ? 'error' : 'default'} size="small" />
          </Stack>
        </>
      )}
    </ReportSection>
  );
}

// O3
function ChannelsCard({ filters }) {
  const { data, loading, error } = useReport(getOutreachChannelsReport, filters);
  const rows = data?.data?.channels || [];
  return (
    <ReportSection
      title="O3 · Channel Mix"
      action={<ReportExportButton path="/reports/outreach/channels/export.csv" filters={filters} filename="outreach-channels.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <TableWithBars
          barColumn="count"
          columns={[
            { key: 'channel', label: 'Channel' },
            { key: 'count', label: 'Volume', align: 'right' },
            { key: 'replyRate', label: 'Reply %', align: 'right', render: (r) => fmtPct(r.replyRate) },
            { key: 'positiveRate', label: 'Positive %', align: 'right', render: (r) => fmtPct(r.positiveRate) },
          ]}
          rows={rows.map((r, i) => ({ id: r.channel + i, ...r }))}
        />
      )}
    </ReportSection>
  );
}

// O4
const OUTCOME_COLORS = { Positive: '#16a34a', Neutral: '#94a3b8', Negative: '#dc2626', NoResponse: '#cbd5e1', FollowUpNeeded: '#f59e0b' };
function StagesCard({ filters }) {
  const { data, loading, error } = useReport(getOutreachStagesReport, filters);
  const rows = data?.data?.stages || [];
  return (
    <ReportSection
      title="O4 · Stage Performance"
      action={<ReportExportButton path="/reports/outreach/stages/export.csv" filters={filters} filename="outreach-stages.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Stack spacing={2}>
          {rows.map((s) => {
            const total = Object.values(s.outcomes || {}).reduce((a, b) => a + b, 0) || 1;
            return (
              <Box key={s.stage}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2">{s.stage}</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtInt(s.count)} interactions</Typography>
                </Stack>
                <Box sx={{ display: 'flex', height: 22, borderRadius: 0.5, overflow: 'hidden' }}>
                  {Object.entries(s.outcomes || {}).map(([k, v]) => {
                    const pct = (v / total) * 100;
                    return pct > 0 ? (
                      <Box key={k} title={`${k}: ${v}`} sx={{ width: `${pct}%`, bgcolor: OUTCOME_COLORS[k] || '#94a3b8' }} />
                    ) : null;
                  })}
                </Box>
                <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                  {Object.entries(s.outcomes || {}).map(([k, v]) => (
                    <Typography key={k} variant="caption" color="text.secondary">
                      <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, bgcolor: OUTCOME_COLORS[k] || '#94a3b8', mr: 0.5, borderRadius: '50%' }} />
                      {k}: {v}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            );
          })}
          {rows.length === 0 && <Typography variant="caption" color="text.secondary">No data.</Typography>}
        </Stack>
      )}
    </ReportSection>
  );
}

// O5
function SentimentCard({ filters }) {
  const { data, loading, error } = useReport(getOutreachSentimentReport, filters);
  const sentiment = data?.data?.sentiment || {};
  const intent = data?.data?.intent || {};
  const avgConf = data?.data?.avgConfidence;
  return (
    <ReportSection title="O5 · Sentiment & Intent">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Sentiment</Typography>
            <DistributionBar
              rows={[
                { label: 'Positive', value: sentiment.Positive || 0, color: '#16a34a' },
                { label: 'Neutral', value: sentiment.Neutral || 0, color: '#94a3b8' },
                { label: 'Negative', value: sentiment.Negative || 0, color: '#dc2626' },
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Intent</Typography>
            <DistributionBar rows={Object.entries(intent).map(([k, v]) => ({ label: k, value: v, color: '#3b82f6' }))} />
          </Grid>
          <Grid item xs={12} md={2}>
            <KpiCard label="Avg confidence" value={avgConf == null ? '—' : avgConf.toFixed(2)} tone="info" />
          </Grid>
        </Grid>
      )}
    </ReportSection>
  );
}

// O6
function FollowUpsCard({ filters }) {
  const { data, loading, error } = useReport(getOutreachFollowUpsReport, filters);
  const d = data?.data || {};
  return (
    <ReportSection title="O6 · Follow-up SLA">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}><KpiCard label="Open" value={fmtInt(d.open)} tone="info" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Overdue" value={fmtInt(d.overdue)} tone={d.overdue > 0 ? 'negative' : 'positive'} /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Completed (window)" value={fmtInt(d.completedInWindow)} tone="positive" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Completion rate" value={fmtPct(d.completionRate)} tone="positive" sub={d.medianHoursToComplete != null ? `Median ${fmtHours(d.medianHoursToComplete)}` : null} /></Grid>
          </Grid>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Per rep</Typography>
          <TableWithBars
            barColumn="open"
            columns={[
              { key: 'name', label: 'Rep' },
              { key: 'open', label: 'Open', align: 'right' },
              { key: 'overdue', label: 'Overdue', align: 'right' },
              { key: 'completionRate', label: 'Compl %', align: 'right', render: (r) => fmtPct(r.completionRate) },
            ]}
            rows={(d.perRep || []).map((r) => ({ id: r.userId ?? r.name, ...r }))}
          />
        </>
      )}
    </ReportSection>
  );
}

// O7
function TimingCard({ filters }) {
  const [metric, setMetric] = useState('replyRate');
  const f = useMemo(() => ({ ...filters, metric, direction: 'Outbound' }), [filters, metric]);
  const { data, loading, error } = useReport(getOutreachTimingReport, f);
  const matrix = data?.data?.matrix || [];
  const hours = Array.from({ length: 17 }, (_, i) => i + 6); // 6 to 22
  const dows = [1, 2, 3, 4, 5, 6, 7];
  return (
    <ReportSection
      title="O7 · Activity Timing Heatmap"
      action={
        <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_, v) => v && setMetric(v)}>
          <ToggleButton value="count">Volume</ToggleButton>
          <ToggleButton value="replyRate">Reply rate</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Heatmap
          rows={hours}
          columns={dows.map((d) => DOW_LABELS[d - 1])}
          rowLabel={(h) => `${String(h).padStart(2, '0')}:00`}
          getValue={(hour, dowLabel) => {
            const dow = DOW_LABELS.indexOf(dowLabel) + 1;
            const cell = matrix.find((c) => c.hour === hour && c.dow === dow);
            if (!cell) return 0;
            return metric === 'replyRate' ? Math.round((cell.value || 0) * 100) : cell.count;
          }}
        />
      )}
    </ReportSection>
  );
}

// O8
function PersonaToneCard({ filters }) {
  const { data, loading, error } = useReport(getOutreachPersonaToneReport, filters);
  const matrix = data?.data?.matrix || [];
  const personas = Array.from(new Set(matrix.map((c) => c.persona)));
  const tones = Array.from(new Set(matrix.map((c) => c.tone)));
  return (
    <ReportSection
      title="O8 · Persona × Tone (reply rate)"
      action={<ReportExportButton path="/reports/outreach/persona-tone/export.csv" filters={filters} filename="outreach-persona-tone.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : matrix.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No recipients in window.</Typography>
      ) : (
        <Heatmap
          rows={personas}
          columns={tones}
          rowLabel={(p) => p}
          getValue={(persona, tone) => {
            const cell = matrix.find((c) => c.persona === persona && c.tone === tone);
            return cell ? Math.round(cell.replyRate * 100) : 0;
          }}
        />
      )}
    </ReportSection>
  );
}

// O9
function FailuresCard({ filters }) {
  const { data, loading, error } = useReport(getOutreachFailuresReport, filters);
  const byState = data?.data?.byState || [];
  const byReason = data?.data?.byReason || [];
  const topDomains = data?.data?.topDomains || [];
  return (
    <ReportSection
      title="O9 · Bounce & Failure Analysis"
      action={<ReportExportButton path="/reports/outreach/failures/export.csv" filters={filters} filename="outreach-failures.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>By delivery state</Typography>
            <DistributionBar rows={byState.map((x) => ({ label: x.name, value: x.count, color: '#dc2626' }))} valueFormatter={fmtInt} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>By reason</Typography>
            <DistributionBar rows={byReason.map((x) => ({ label: x.name, value: x.count, color: '#f59e0b' }))} valueFormatter={fmtInt} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Top failing domains</Typography>
            <DistributionBar rows={topDomains.map((x) => ({ label: x.name, value: x.count }))} valueFormatter={fmtInt} />
          </Grid>
        </Grid>
      )}
    </ReportSection>
  );
}

// O10
function RepsCard({ filters }) {
  const { data, loading, error } = useReport(getOutreachRepsReport, filters);
  const rows = data?.data?.reps || [];
  return (
    <ReportSection
      title="O10 · Rep / Sender Performance"
      action={<ReportExportButton path="/reports/outreach/reps/export.csv" filters={filters} filename="outreach-reps.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <TableWithBars
          barColumn="sent"
          columns={[
            { key: 'name', label: 'Rep' },
            { key: 'sent', label: 'Sent', align: 'right' },
            { key: 'replies', label: 'Replies', align: 'right' },
            { key: 'replyRate', label: 'Reply %', align: 'right', render: (r) => fmtPct(r.replyRate) },
            { key: 'positiveRate', label: 'Positive %', align: 'right', render: (r) => fmtPct(r.positiveRate) },
          ]}
          rows={rows.map((r) => ({ id: r.userId ?? r.name, ...r }))}
        />
      )}
    </ReportSection>
  );
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'campaigns', label: 'Campaigns (O1)' },
  { key: 'funnel', label: 'Funnel (O2)' },
  { key: 'channels', label: 'Channels (O3)' },
  { key: 'stages', label: 'Stages (O4)' },
  { key: 'sentiment', label: 'Sentiment (O5)' },
  { key: 'follow-ups', label: 'Follow-ups (O6)' },
  { key: 'timing', label: 'Timing (O7)' },
  { key: 'persona', label: 'Persona (O8)' },
  { key: 'failures', label: 'Failures (O9)' },
  { key: 'reps', label: 'Reps (O10)' },
];

export default function OutreachReportPage() {
  const [filters, setFilters] = useState({ window: '30d', includeArchived: false });
  const [tab, setTab] = useState('overview');
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <ChatBubbleOutlineOutlinedIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Outreach & Activity Reports</Typography>
        <Box flex={1} />
        <ReportFilterBar value={filters} onChange={setFilters} />
      </Stack>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>
      {tab === 'overview' && (
        <>
          <CampaignsCard filters={filters} />
          <FunnelCard filters={filters} />
          <ChannelsCard filters={filters} />
        </>
      )}
      {tab === 'campaigns' && <CampaignsCard filters={filters} />}
      {tab === 'funnel' && <FunnelCard filters={filters} />}
      {tab === 'channels' && <ChannelsCard filters={filters} />}
      {tab === 'stages' && <StagesCard filters={filters} />}
      {tab === 'sentiment' && <SentimentCard filters={filters} />}
      {tab === 'follow-ups' && <FollowUpsCard filters={filters} />}
      {tab === 'timing' && <TimingCard filters={filters} />}
      {tab === 'persona' && <PersonaToneCard filters={filters} />}
      {tab === 'failures' && <FailuresCard filters={filters} />}
      {tab === 'reps' && <RepsCard filters={filters} />}
    </Box>
  );
}
